/**
 * Legacy program-router logic keyed off workbook-shaped rows (same shape as
 * src/data/programRouterRows.json). Extracted so parity tests and fallback
 * behaviour stay identical while public/programRouterMatch.js loads catalog
 * rows from Supabase when available.
 */
import routerRows from '../data/programRouterRows.json' with { type: 'json' };

function bool(v) {
  return String(v).toUpperCase() === 'TRUE';
}

function purposesList(row) {
  return String(row.allowedPurposes || '')
    .split('|')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export function routePriorityNum(row) {
  const n = parseInt(row.routePriority, 10);
  return Number.isFinite(n) ? n : 999;
}

function matchesOccupancy(row, a) {
  const u = a.propertyUse;
  if (u === 'primary') return bool(row.primaryAllowed);
  if (u === 'secondary') return bool(row.secondHomeAllowed);
  if (u === 'investment') return bool(row.investmentAllowed);
  return false;
}

function matchesAllowedPurposes(row, a, purposes) {
  if (!purposes.length) return false;
  const po = a.purchaseOrRefinance;
  const rg = a.refiGoal || 'not-sure';

  if (po === 'purchase') {
    return purposes.some(
      (p) =>
        p.includes('purchase') ||
        p.includes('construction') ||
        (p.includes('improvement') && !p.includes('refi'))
    );
  }

  if (rg === 'cash-out') {
    return purposes.some((p) => p.includes('cash-out'));
  }

  if (purposes.some((p) => p.includes('existing usda'))) {
    return rg === 'usda-streamline' && a.existingUSDALoan === 'yes';
  }

  if (purposes.some((p) => p.includes('rate reduction refi of prior nadl'))) {
    return a.priorNADL === 'yes';
  }

  if (rg === 'limited-cash-out') {
    return purposes.some(
      (p) =>
        p.includes('limited cash-out') ||
        p.includes('rate-term') ||
        p.includes('no cash-out refi')
    );
  }

  if (rg === 'usda-streamline') {
    return purposes.some((p) => p.includes('existing usda'));
  }

  if (rg === 'rate-term' || rg === 'not-sure') {
    return purposes.some(
      (p) =>
        p.includes('rate-term') ||
        p.includes('limited cash-out') ||
        p.includes('no cash-out refi') ||
        (p.includes('refi') && !p.includes('cash-out'))
    );
  }

  return false;
}

function matchesUsda(row, a, purposes) {
  if (!bool(row.USDAEligibleAreaRequired)) return true;
  if (a.propertyUse !== 'primary') return false;
  if (a.ruralProperty !== 'yes') return false;
  if (purposes.some((p) => p.includes('existing usda'))) {
    return a.purchaseOrRefinance === 'refinance' && a.refiGoal === 'usda-streamline';
  }
  return a.purchaseOrRefinance === 'purchase';
}

function matchesTribal184(row, a) {
  if (!bool(row.tribalEligibilityRequired)) return true;
  return a.tribalHousingPath === 'section184';
}

function matchesNadl(row, a) {
  if (row.routerProgramCode !== 'NADL_TRUST_LAND') return true;
  return a.tribalHousingPath === 'nadl' && a.militaryService === 'yes';
}

function matchesLoanSizeBand(row, a) {
  const tier = a.loanSizeTier || 'conforming';
  const code = row.routerProgramCode;

  if (code === 'JUMBO_FULL_DOC') return tier === 'jumbo';

  if (tier === 'jumbo') {
    return code.startsWith('NONQM_') || code === 'JUMBO_FULL_DOC';
  }

  if (tier === 'conforming') {
    if (code === 'CONV_STD_HIGH_BALANCE') return false;
    if (code === 'REFINOW' || code === 'REFI_POSSIBLE') return true;
    return code !== 'JUMBO_FULL_DOC';
  }

  if (tier === 'high_balance') {
    if (code === 'REFINOW' || code === 'REFI_POSSIBLE') return false;
    if (code === 'CONV_STD_CONFORMING') return false;
    return code !== 'JUMBO_FULL_DOC';
  }

  return true;
}

function matchesRenovation(row, a) {
  const t = String(row.renovationProgramType || '').trim();
  if (!t) return true;
  const need = a.renovationNeeds || 'none';
  if (need === 'none') return false;
  if (t.toLowerCase().includes('limited') || t.includes('203(k) Limited')) {
    return need === 'minor' || need === 'major' || need === 'not-sure';
  }
  if (t.toLowerCase().includes('standard') && t.includes('203')) {
    return need === 'major' || need === 'not-sure';
  }
  return need !== 'none';
}

function matchesAgencyRefiSpecial(row, a) {
  const code = row.routerProgramCode;
  if (code !== 'REFINOW' && code !== 'REFI_POSSIBLE') return true;
  if (a.purchaseOrRefinance !== 'refinance') return false;
  if (a.refiGoal !== 'rate-term' && a.refiGoal !== 'not-sure' && a.refiGoal !== 'limited-cash-out') {
    return false;
  }
  if (a.loanSizeTier !== 'conforming') return false;
  if (a.propertyUse !== 'primary') return false;
  if (a.propertyType && a.propertyType !== 'single-family') return false;
  if (code === 'REFINOW' && a.agencyLoanServicer !== 'fannie') return false;
  if (code === 'REFI_POSSIBLE' && a.agencyLoanServicer !== 'freddie') return false;
  return (
    a.refiNowLowIncomeHint === 'yes' ||
    a.refiNowLowIncomeHint === 'maybe'
  );
}

function matchesAffordable(row, a) {
  if (row.routerProgramCode !== 'AFFORDABLE_AGENCY_CONV') return true;
  if (a.affordableProgramInterest === 'yes') return true;
  if (a.firstTimeHomeBuyer === 'yes' && a.propertyUse === 'primary') return true;
  return false;
}

function matchesNonQm(row, a) {
  const code = row.routerProgramCode;
  if (!code.startsWith('NONQM_')) return true;

  if (code === 'NONQM_DSCR') {
    return a.propertyUse === 'investment';
  }
  if (code === 'NONQM_BANK_STMT') {
    return (
      a.employmentStatus === 'self-employed' &&
      a.incomeDocumentation === 'bank-statements'
    );
  }
  if (code === 'NONQM_ASSET_QUAL') {
    return a.incomeDocumentation === 'assets';
  }
  if (code === 'NONQM_ALT_FLEX') {
    return a.nonQmFlexInterest === 'yes';
  }
  return true;
}

function matchesPropertyTypeUnits(row, a) {
  const maxU = parseInt(row.maxUnits, 10);
  if (maxU === 1 && a.propertyType && a.propertyType !== 'single-family') {
    return false;
  }
  return true;
}

function matchesConvRenoRefi(row, a) {
  if (row.routerProgramCode !== 'CONV_RENOVATION') return true;
  const purposes = purposesList(row);
  const hasLimited = purposes.some((p) => p.includes('limited cash-out'));
  const hasNoCash = purposes.some((p) => p.includes('no cash-out refi'));
  if (a.purchaseOrRefinance !== 'refinance') return true;
  if (a.refiGoal === 'cash-out') return false;
  if (hasLimited && !hasNoCash) {
    return a.refiGoal === 'limited-cash-out' || a.refiGoal === 'rate-term' || a.refiGoal === 'not-sure';
  }
  if (hasNoCash && !hasLimited) {
    return a.refiGoal === 'rate-term' || a.refiGoal === 'not-sure';
  }
  return true;
}

/** Exported for tests / hybrid matchers using catalog `source_row`. */
export function rowMatchesRouter(row, a) {
  const purposes = purposesList(row);
  if (!matchesAllowedPurposes(row, a, purposes)) return false;
  if (!matchesOccupancy(row, a)) return false;
  if (bool(row.veteranEligibilityRequired) && a.militaryService !== 'yes') return false;
  if (!matchesUsda(row, a, purposes)) return false;
  if (bool(row.stateHFAParticipationRequired) && a.stateHfaParticipant !== 'yes') {
    return false;
  }
  if (!matchesTribal184(row, a)) return false;
  if (!matchesNadl(row, a)) return false;
  if (!matchesLoanSizeBand(row, a)) return false;
  if (!matchesRenovation(row, a)) return false;
  if (!matchesAgencyRefiSpecial(row, a)) return false;
  if (!matchesAffordable(row, a)) return false;
  if (!matchesNonQm(row, a)) return false;
  if (!matchesPropertyTypeUnits(row, a)) return false;
  if (!matchesConvRenoRefi(row, a)) return false;
  return true;
}

export function pickVariant(rows, a) {
  let pool = [...rows];
  const prefRate = a.rateTypePreference;
  const prefTerm = a.loanTermPreference;
  if (prefRate === 'fixed') pool = pool.filter((r) => r.rateType === 'Fixed');
  if (prefRate === 'arm') pool = pool.filter((r) => r.rateType === 'ARM');
  if (prefTerm && prefTerm !== 'not-sure') {
    const t = parseInt(prefTerm, 10);
    const exact = pool.filter((r) => parseInt(r.amortizationTermYears, 10) === t);
    if (exact.length) pool = exact;
  }
  const fixed30 = pool.find(
    (r) => r.rateType === 'Fixed' && parseInt(r.amortizationTermYears, 10) === 30
  );
  return fixed30 || pool.sort((x, y) => routePriorityNum(x) - routePriorityNum(y))[0];
}

function inferMinCredit(row) {
  const code = row.routerProgramCode || '';
  if (code.startsWith('FHA')) return 580;
  if (code.includes('VA') || row.productFamily === 'Indian Housing') return 580;
  if (code === 'JUMBO_FULL_DOC') return 680;
  if (code.startsWith('NONQM_')) return 600;
  return 620;
}

function inferMinDown(row) {
  const code = row.routerProgramCode || '';
  if (code === 'VA_STD' || code === 'VA_ALTERATION_REPAIR') return 0;
  if (code === 'USDA_STD') return 0;
  if (code.startsWith('FHA')) return 3.5;
  if (code.startsWith('AFFORDABLE')) return 3;
  return 5;
}

function inferMaxLtv(row) {
  const code = row.routerProgramCode || '';
  if (code === 'VA_STD' || code === 'VA_ALTERATION_REPAIR') return 100;
  if (code === 'USDA_STD') return 100;
  if (code.startsWith('FHA')) return 96.5;
  return 97;
}

function buildRationale(row) {
  const parts = [];
  if (row.routingNotes) parts.push(row.routingNotes);
  if (row.qualifyingMethod) {
    parts.push(`Qualifying: ${row.qualifyingMethod}`);
  }
  return parts.slice(0, 4);
}

/** Base UI shape (before Owen enrichment in programRouterMatch.js). */
export function mapRowToProgramBase(row) {
  const armYears = parseInt(row.armInitialFixedYears, 10);
  const amo = parseInt(row.amortizationTermYears, 10) || 30;
  const isArm = row.rateType === 'ARM';
  const rp = routePriorityNum(row);
  return {
    id: row.rawProgramCode,
    rawProgramCode: row.rawProgramCode,
    routerProgramCode: row.routerProgramCode,
    name: row.clientFacingProgram,
    displayName:
      row.productSubFamily && row.productSubFamily !== 'Standard'
        ? `${row.clientFacingProgram} (${row.productSubFamily})`
        : row.clientFacingProgram,
    productFamily: row.productFamily,
    qualifyingMethod: row.qualifyingMethod || '',
    routingNotes: row.routingNotes || '',
    routePriority: rp,
    tieBreakGroup: row.tieBreakGroup || '',
    rateType: row.rateType || 'Fixed',
    fixedPeriod: isArm ? armYears || 5 : amo,
    amortization: amo,
    armIndex:
      row.rateType === 'ARM'
        ? `ARM (${armYears || '?'} yr fixed, then adjusts)`
        : '',
    score: Math.max(5, 98 - Math.min(rp, 90)),
    rationale: buildRationale(row),
    minCreditScore: inferMinCredit(row),
    minDownPayment: inferMinDown(row),
    maxLTV: inferMaxLtv(row),
    requiresMilitary: bool(row.veteranEligibilityRequired),
    requiresRural: bool(row.USDAEligibleAreaRequired),
    renovationProgram: Boolean(String(row.renovationProgramType || '').trim()),
    investmentOnly: row.routerProgramCode === 'NONQM_DSCR',
    selfEmployed: row.routerProgramCode === 'NONQM_BANK_STMT',
    investor: row.investor || '',
  };
}

export function groupByCode(rows) {
  const m = new Map();
  for (const row of rows) {
    const k = row.routerProgramCode;
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(row);
  }
  return m;
}

export function getBundledRouterRows() {
  return routerRows;
}

/** Bit-identical to historical matchProgramsFromRouter (bundled JSON only). */
export function matchProgramsFromRouterFromBundledJson(answers) {
  const a = answers || {};
  const matched = routerRows.filter((row) => rowMatchesRouter(row, a));
  const groups = groupByCode(matched);
  const orderedCodes = [...groups.keys()].sort((c1, c2) => {
    const p1 = routePriorityNum(groups.get(c1)[0]);
    const p2 = routePriorityNum(groups.get(c2)[0]);
    return p1 - p2;
  });

  const picks = [];
  for (const code of orderedCodes) {
    if (picks.length >= 8) break;
    const variant = pickVariant(groups.get(code), a);
    if (variant) picks.push(mapRowToProgramBase(variant));
  }

  const top = picks.slice(0, 3);
  if (top.length > 0) return top;

  const fallback = routerRows.filter(
    (r) =>
      r.routerProgramCode === 'CONV_STD_CONFORMING' &&
      r.rateType === 'Fixed' &&
      parseInt(r.amortizationTermYears, 10) === 30
  );
  const fb = pickVariant(fallback, a);
  return fb ? [mapRowToProgramBase(fb)] : [];
}
